import React from 'react';
import { LayoutArea } from '../types';

/**
 * Component cache for fast tab switching and improved performance.
 * Keeps rendered components in memory to avoid re-mounting when switching tabs.
 */
class ComponentCache {
  private cache = new Map<string, React.ReactElement>();
  private accessTimes = new Map<string, number>();
  private maxCacheSize = 50; // Prevent memory leaks

  /**
   * Creates a cache key for a component
   */
  private createKey(area: LayoutArea, contentId: string, source?: string): string {
    return `${area}:${contentId}:${source || 'default'}`;
  }

  /**
   * Gets a cached component if it exists
   */
  get(area: LayoutArea, contentId: string, source?: string): React.ReactElement | undefined {
    const key = this.createKey(area, contentId, source);
    const cached = this.cache.get(key);
    
    if (cached) {
      this.accessTimes.set(key, Date.now());
      return cached;
    }
    
    return undefined;
  }

  /**
   * Stores a component in the cache
   */
  set(area: LayoutArea, contentId: string, component: React.ReactElement, source?: string): void {
    const key = this.createKey(area, contentId, source);
    
    // Clean up old entries if cache is getting too large
    if (this.cache.size >= this.maxCacheSize) {
      this.evictOldestEntries();
    }
    
    this.cache.set(key, component);
    this.accessTimes.set(key, Date.now());
  }

  /**
   * Removes a specific cached component
   */
  remove(area: LayoutArea, contentId: string, source?: string): void {
    const key = this.createKey(area, contentId, source);
    this.cache.delete(key);
    this.accessTimes.delete(key);
  }

  /**
   * Clears all cached components for a specific area
   */
  clearArea(area: LayoutArea): void {
    const keysToRemove: string[] = [];
    
    for (const key of this.cache.keys()) {
      if (key.startsWith(`${area}:`)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      this.cache.delete(key);
      this.accessTimes.delete(key);
    });
  }

  /**
   * Clears all cached components
   */
  clear(): void {
    this.cache.clear();
    this.accessTimes.clear();
  }

  /**
   * Evicts the oldest entries when cache is full
   */
  private evictOldestEntries(): void {
    const entries = Array.from(this.accessTimes.entries())
      .sort(([, timeA], [, timeB]) => timeA - timeB)
      .slice(0, 10); // Remove oldest 10 entries

    entries.forEach(([key]) => {
      this.cache.delete(key);
      this.accessTimes.delete(key);
    });
  }

  /**
   * Gets cache statistics for debugging
   */
  getStats(): { size: number; maxSize: number; areas: string[] } {
    const areas = new Set<string>();
    this.cache.forEach((_, key) => {
      const area = key.split(':')[0];
      areas.add(area);
    });

    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      areas: Array.from(areas),
    };
  }
}

// Global component cache instance
export const componentCache = new ComponentCache();

/**
 * React hook for using the component cache
 */
export function useComponentCache() {
  return {
    get: componentCache.get.bind(componentCache),
    set: componentCache.set.bind(componentCache),
    remove: componentCache.remove.bind(componentCache),
    clearArea: componentCache.clearArea.bind(componentCache),
    clear: componentCache.clear.bind(componentCache),
    getStats: componentCache.getStats.bind(componentCache),
  };
}
